import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FlushProgress } from '../../entities/survey.entity';
import { SurveyOfflineSyncService } from '../../services/survey-offline-sync.service';

@Component({
  selector: 'app-sync-report-modal',
  templateUrl: './sync-report-modal.component.html',
})
export class SyncReportModalComponent implements OnDestroy {
  progress: FlushProgress = {
    total: 0,
    synced: 0,
    failed: 0,
    remaining: 0,
    phase: 'running',
  };
  isPausing = false;
  private readonly progressSub: Subscription;

  constructor(
    public dialogRef: MatDialogRef<SyncReportModalComponent>,
    private offlineSync: SurveyOfflineSyncService,
    private cdr: ChangeDetectorRef
  ) {
    this.progress = this.offlineSync.flushProgress;
    this.progressSub = this.offlineSync.flushProgress$.subscribe((progress) => {
      this.progress = progress;
      if (progress.phase !== 'running') {
        this.isPausing = false;
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.progressSub.unsubscribe();
  }

  get isRunning(): boolean {
    return this.progress.phase === 'running';
  }

  get title(): string {
    if (this.isRunning) {
      return this.isPausing ? 'Pausing...' : 'Syncing...';
    }
    if (this.progress.phase === 'paused') {
      return 'Sync paused';
    }
    if (this.progress.failed === 0 && this.progress.remaining === 0) {
      return 'Sync complete';
    }
    if (this.progress.synced === 0) {
      return 'Sync failed';
    }
    return 'Sync incomplete';
  }

  get message(): string {
    if (this.isRunning) {
      return 'Do not close this page while submissions are sending.';
    }
    if (this.progress.phase === 'paused') {
      return 'Remaining submissions stayed on this device. Tap Sync now when you want to continue.';
    }
    if (this.progress.failed === 0 && this.progress.remaining === 0) {
      if (this.progress.synced === 0) {
        return 'No pending submissions.';
      }
      return 'All queued submissions were sent successfully.';
    }

    return 'Failed and remaining submissions stayed on this device. Tap Sync now again when the network is better.';
  }

  get progressPercent(): number {
    if (this.progress.total <= 0) {
      return 0;
    }
    return (
      ((this.progress.synced + this.progress.failed) / this.progress.total) * 100
    );
  }

  get syncedPercent(): number {
    if (this.progress.total <= 0) {
      return 0;
    }
    return (this.progress.synced / this.progress.total) * 100;
  }

  get failedPercent(): number {
    if (this.progress.total <= 0) {
      return 0;
    }
    return (this.progress.failed / this.progress.total) * 100;
  }

  get displayPercent(): number {
    return Math.round(this.progressPercent);
  }

  get headingIcon(): string {
    if (this.isRunning) {
      return 'refresh-cw';
    }
    if (this.progress.phase === 'paused') {
      return 'octagon-pause';
    }
    if (this.progress.failed === 0 && this.progress.remaining === 0) {
      return 'check-circle-2';
    }
    if (this.progress.synced === 0) {
      return 'x-circle';
    }
    return 'triangle-alert';
  }

  get headingIconWrapClass(): string {
    if (this.isRunning) {
      return 'bg-sky-50 text-sky-700 ring-sky-100';
    }
    if (this.progress.phase === 'paused') {
      return 'bg-amber-50 text-amber-700 ring-amber-100';
    }
    if (this.progress.failed === 0 && this.progress.remaining === 0) {
      return 'bg-green-50 text-green-700 ring-green-100';
    }
    if (this.progress.synced === 0) {
      return 'bg-red-50 text-red-700 ring-red-100';
    }
    return 'bg-amber-50 text-amber-700 ring-amber-100';
  }

  onPause(): void {
    if (!this.isRunning || this.isPausing) {
      return;
    }
    this.isPausing = true;
    this.offlineSync.pauseFlush();
    this.cdr.detectChanges();
  }

  onClose(): void {
    if (this.isRunning) {
      return;
    }
    this.dialogRef.close();
  }
}
